/**
 * Unified Analytics
 *
 * Tracks events across all analytics platforms (GA + Amplitude)
 */

import { BrowserClient } from '@amplitude/analytics-browser/lib/esm/types'
import { initAmplitude, track, type InitAmplitudeOptions } from './amplitude'
import { emitGAEvent } from './google-analytics'
import { AnalyticsEventPayload, PropertyValue } from './types'

const AUTOCAPTURE_OFF: InitAmplitudeOptions['autocapture'] = {
    attribution: false,
    fileDownloads: false,
    formInteractions: false,
    pageViews: false,
    sessions: false,
    elementInteractions: false,
    frustrationInteractions: false,
    networkTracking: false,
    webVitals: false,
    pageUrlEnrichment: false,
}

const amplitudeSquadronApiKey = process.env.REACT_APP_AMPLITUDE_API_KEY
export let amplitudeSquadronInstance: BrowserClient | undefined
if (amplitudeSquadronApiKey) amplitudeSquadronInstance = initAmplitude(amplitudeSquadronApiKey, { autocapture: AUTOCAPTURE_OFF })

// Session-level event context (company_id, mc_number, …) merged into every unified event so
// per-company attribution never depends on individual call sites remembering to pass it.
// Kept event-level (not only user properties) so it stays correct across multi-MC company switches.
let analyticsContext: Record<string, PropertyValue> = {}

export const setAnalyticsContext = (context: Record<string, PropertyValue | undefined>): void => {
    const next = { ...analyticsContext }
    for (const [key, value] of Object.entries(context)) {
        if (value === undefined || value === '') delete next[key]
        else next[key] = value
    }
    analyticsContext = next
}

export const resetAnalyticsContext = (): void => {
    analyticsContext = {}
}

/** Read-only snapshot for feature-owned storage scoping; callers cannot mutate global context. */
export const getAnalyticsContext = (): Readonly<Record<string, PropertyValue>> => ({ ...analyticsContext })

/**
 * Tracks a single event across all analytics platforms.
 * Sends to Google Analytics and Squadron Squad Amplitude.
 * Explicit event properties always win over the session context.
 */
export const trackUnifiedEvent = (event: AnalyticsEventPayload): void => {
    const { action, category, ...rest } = event
    const eventName = String(action)
    // category after the context spread so a (mis)registered context key can never clobber it.
    const properties = { ...analyticsContext, category, ...(rest as Record<string, PropertyValue>) }

    try {
        emitGAEvent(eventName, properties)
    } catch {
        /* GA must never break the app */
    }

    if (typeof window !== 'undefined' && amplitudeSquadronInstance) {
        try {
            track(eventName, properties, amplitudeSquadronInstance, { skipGA: true })
        } catch (error) {
            console.error('Failed to track event in Squadron Amplitude:', error)
        }
    }
}
